import i18next from "i18next";
import { useContext } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";
import { TrackingContext } from "context/Tracking";

import { getAIRiskApi } from "../api";
import { AIHazardPatch, AIHazardShell, AIHazardStatus } from "../types";
import { applyHazardPatchToSessionCache } from "./sessionCache";

interface UpdateAIHazardVariables {
  hazardId: string;
  patch: AIHazardPatch;
}

// PATCH /hazards/:hazardId — shell edits, dismiss/restore transitions,
// diff merges and proposal discards. The returned shell is merged into the
// cached session.
export const useUpdateAIHazard = (projectId: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { track } = useContext(TrackingContext);
  const { displayNotification } = useContext(ControlContext);

  return useMutation<AIHazardShell, Error, UpdateAIHazardVariables>(
    ({ hazardId, patch }) =>
      getAIRiskApi(client).updateHazard(projectId, hazardId, patch),
    {
      onSuccess: (hazard, { hazardId, patch }) => {
        applyHazardPatchToSessionCache(
          queryClient,
          projectId,
          hazardId,
          patch,
          hazard
        );

        // Status PATCHes exist only for dismiss/restore (state machine §1.1).
        if (patch.status === AIHazardStatus.dismissed) {
          track("ai risk: hazard dismissed");
        } else if (
          patch.status === AIHazardStatus.suggested ||
          patch.status === AIHazardStatus.generated
        ) {
          track("ai risk: hazard restored");
        }
      },
      onError: (error) =>
        displayNotification!(
          i18next.t("risks.ai.errors.saveFailed"),
          error.message,
          "error"
        ),
    }
  );
};
